/**
 * Os destinos de reserva — a allowlist.
 *
 * Por que isto é uma lista fechada e não um campo de texto no formulário do link: o
 * redirecionador nunca aceita destino vindo de fora (invariante 7). A URL entra uma vez,
 * validada e auditada, e daí em diante o link escolhe um ITEM desta lista pelo id. Um campo
 * de URL solta no formulário seria a porta aberta de open redirect que o nosso próprio
 * domínio assinaria.
 */
import { useEffect } from 'react';
import { Button, Drawer, EmptyState, Field, Input } from '@mgdlead/ui';
import { Link2, Trash2 } from 'lucide-react';

import { FormAlert } from '@mgdlead/ui';
import { allowlistUrlSchema } from '../../../lib/schemas';
import type { AllowlistUrlValues } from '../../../lib/schemas';
import { useZodForm } from '../../../lib/useZodForm';
import type { RedirectorAllowlistUrl } from '../../../lib/api';

export interface AllowlistDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  urls: readonly RedirectorAllowlistUrl[];
  canUpdate: boolean;
  removing: boolean;
  onAdd: (values: { label: string; url: string }) => Promise<void>;
  onRemove: (allowlistUrlId: string) => void;
}

const FORM_ID = 'allowlist-form';

export function AllowlistDrawer({
  open,
  onOpenChange,
  urls,
  canUpdate,
  removing,
  onAdd,
  onRemove,
}: AllowlistDrawerProps) {
  const form = useZodForm({
    schema: allowlistUrlSchema,
    initialValues: { label: '', url: '' } satisfies AllowlistUrlValues,
    onSubmit: async (values) => {
      await onAdd({ label: values.label, url: values.url });
      form.reset({ label: '', url: '' });
    },
  });

  const { reset } = form;
  useEffect(() => {
    if (open) reset({ label: '', url: '' });
  }, [open, reset]);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Destinos de reserva"
      description="Para onde um link manda a pessoa quando nenhum grupo da campanha tem vaga."
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Um link só pode cair em endereços que estão nesta lista. É o que impede que alguém
          transforme o seu domínio de redirecionamento num atalho para qualquer lugar da internet.
        </p>

        {urls.length === 0 ? (
          <EmptyState
            kind="zero"
            icon={Link2}
            compact
            title="Nenhum destino autorizado"
            description="Sem reserva, quem clicar quando não houver grupo com vaga vê uma página de indisponível. O clique continua sendo registrado."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {urls.map((url) => (
              <li key={url.id} className="flex items-start justify-between gap-2 py-2">
                <span className="flex min-w-0 flex-col">
                  <span className="text-sm font-medium">{url.label}</span>
                  <span className="break-all font-mono text-2xs text-muted-foreground">
                    {url.url}
                  </span>
                </span>
                {canUpdate ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={<Trash2 />}
                    aria-label={`Remover destino ${url.label}`}
                    disabled={removing}
                    onClick={() => onRemove(url.id)}
                  >
                    Remover
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canUpdate ? (
          <form
            id={FORM_ID}
            noValidate
            onSubmit={form.handleSubmit}
            className="flex flex-col gap-3 border-t border-border pt-4"
          >
            <h3 className="text-md font-medium">Autorizar um destino</h3>
            {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

            <Field
              label="Nome"
              hint="Como você reconhece este destino na lista."
              error={form.errors.label}
              required
            >
              {(control) => (
                <Input
                  {...control}
                  name="label"
                  autoComplete="off"
                  placeholder="Lista de espera"
                  value={form.values.label}
                  invalid={form.errors.label !== undefined}
                  onChange={(event) => form.setField('label', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Endereço"
              hint="Precisa começar com http:// ou https://"
              error={form.errors.url}
              required
            >
              {(control) => (
                <Input
                  {...control}
                  name="url"
                  autoComplete="off"
                  inputMode="url"
                  placeholder="https://seusite.com/lista-de-espera"
                  value={form.values.url}
                  invalid={form.errors.url !== undefined}
                  onChange={(event) => form.setField('url', event.target.value)}
                />
              )}
            </Field>

            <Button
              variant="primary"
              type="submit"
              loading={form.pending}
              loadingLabel="Salvando"
              block
            >
              Autorizar destino
            </Button>
          </form>
        ) : null}
      </div>
    </Drawer>
  );
}
